
import { normalizeUrl } from '@univdiam/ui/lib/UrlNormalize';
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Button } from '../../components/button';
import { Card, CardContent, CardHeader } from '../../components/card';
import { Input } from '@univdiam/ui/components/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@univdiam/ui/components/select';
import { ChevronLeft, ChevronRight, Loader2, Search, X } from 'lucide-react';

interface CatalogItem {
  id: number
  imageUrl: string
  thumbUrl?: string
  price: number
  type: string
  sku: string
  title: string
}

export type ExploreCatalogParams = { page: number; limit: number; search: string; type: string }

export type ExploreCatalogPage = {
  items: CatalogItem[]
  types: string[]
  pagination: { totalItems: number; totalPages: number; currentPage: number }
}

interface ExploreCatalogProps {
  handleNavigate: (sku: string) => void
  // Each app has its own api client, so the fetch is passed in rather than imported.
  fetchCatalog: (params: ExploreCatalogParams) => Promise<ExploreCatalogPage>
}

const ALL_TYPES = '__all__'
const PAGE_SIZE = 20

const ExploreCatalog: React.FC<ExploreCatalogProps> = ({ handleNavigate, fetchCatalog }) => {
  // Page, search and type live in the URL so Back from a product returns to
  // the same page of the same results.
  const [params, setParams] = useSearchParams()
  const page = Math.max(Number(params.get('page')) || 1, 1)
  const search = params.get('q') ?? ''
  const type = params.get('type') ?? ''

  const topRef = useRef<HTMLDivElement>(null)
  const [searchInput, setSearchInput] = useState(search)

  const update = (changes: Record<string, string>) =>
    setParams(prev => {
      const next = new URLSearchParams(prev)
      for (const [key, value] of Object.entries(changes)) {
        if (value) next.set(key, value)
        else next.delete(key)
      }
      return next
    }, { replace: true })

  useEffect(() => {
    if (searchInput === search) return
    const t = setTimeout(() => update({ q: searchInput.trim() ? searchInput : '', page: '' }), 300)
    return () => clearTimeout(t)
  }, [searchInput])

  const { data, isLoading, isError, isPlaceholderData } = useQuery({
    queryKey: ['catalog-categories', page, search, type],
    queryFn: () => fetchCatalog({ page, limit: PAGE_SIZE, search, type }),
    placeholderData: keepPreviousData
  })

  const items = data?.items ?? []
  const totalPages = data?.pagination?.totalPages ?? 0
  const totalItems = data?.pagination?.totalItems ?? 0

  const goToPage = (target: number) => {
    update({ page: target > 1 ? String(target) : '' })
    topRef.current?.scrollIntoView({ block: 'start' })
  }

  return (
    <Card ref={topRef} className="rounded-md border border-border m-4 md:m-[24px] py-0 gap-0 scroll-mt-4">
      <CardHeader className="flex flex-col gap-3 md:flex-row md:justify-between md:items-center px-4 md:px-5 py-3.5 border-b border-border">
        <h1 className="font-semibold text-heading5">Catalog</h1>

        {/* Search + Filter */}
        <div className="flex flex-col sm:flex-row items-stretch gap-2.5 w-full md:w-auto">
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by title, SKU or type"
              className="h-10 rounded-lg border-border bg-muted/40 pl-10 pr-9 text-body2 shadow-none transition-colors focus-visible:bg-background"
            />
            {isPlaceholderData ? (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
            ) : searchInput ? (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => setSearchInput('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>
          <Select
            value={type || ALL_TYPES}
            onValueChange={(v) => update({ type: v === ALL_TYPES ? '' : v, page: '' })}
          >
            <SelectTrigger className="h-10 w-full rounded-lg border-border bg-muted/40 text-body2 shadow-none sm:w-48">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_TYPES}>All types</SelectItem>
              {(data?.types ?? []).map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>

      {/* Catalog Grid */}
      {isLoading ? (
        <div className="flex h-64 w-full items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : isError ? (
        <div className="px-4 py-16 text-center text-body2 text-muted-foreground">
          Could not load the catalog. Please try again.
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 px-4 py-16 text-center">
          <Search className="h-8 w-8 text-muted-foreground/50" />
          <p className="text-body2 text-muted-foreground">No products match your search.</p>
        </div>
      ) : (
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5 p-4 md:p-5">
          {items.map((item) => (
            <Card
              key={item.sku}
              className="group cursor-pointer ring-0 shadow-none py-0 gap-0 rounded-none"
              onClick={() => handleNavigate(item.sku)}
            >
              {/* Image container with aspect ratio */}
              <div className="relative aspect-square w-full overflow-hidden rounded-md">
                <img
                  src={normalizeUrl(item.thumbUrl || item.imageUrl)}
                  alt={item.title || `Catalog item ${item.id}`}
                  loading="lazy"
                  decoding="async"
                  onError={(e) => {
                    // Thumb missing (older seed) — fall back to full-res image
                    if (item.thumbUrl && e.currentTarget.src !== normalizeUrl(item.imageUrl)) {
                      e.currentTarget.src = normalizeUrl(item.imageUrl)
                    }
                  }}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
              </div>

              {/* Content section */}
              <CardContent className="px-0 mt-4">
                <h3 className="text-body1 font-semibold">{item.title || item.sku}</h3>
                <p className="text-muted-foreground text-caption1">{item.type}</p>
                <p className="text-muted-foreground mt-1">
                  From ${Number(item.price).toLocaleString('en-US')}
                </p>
              </CardContent>
            </Card>
          ))}
        </CardContent>
      )}

      {(totalPages > 1 || page > 1) && (
        <div className="flex items-center justify-between gap-3 border-t border-border p-4 md:p-5">
          <p className="text-body2 text-muted-foreground">
            Page {page} of {totalPages} · {totalItems} products
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => goToPage(page - 1)}
              disabled={page <= 1 || isPlaceholderData}
            >
              <ChevronLeft className="h-4 w-4" />
              Prev
            </Button>
            <Button
              variant="outline"
              onClick={() => goToPage(page + 1)}
              disabled={page >= totalPages || isPlaceholderData}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </Card>
  )
}

export default ExploreCatalog
